import { useEffect, useMemo, useState } from 'react';

import { isEqual } from 'lodash';

import { LoadedFile } from '../../../../interfaces/LoadedFile';

export type UseUploadImageReturn = {
  image: LoadedFile | null;
  isLoading: boolean | undefined;
  isChanged: boolean;
  handleUploadImage: (acceptedFiles: File[]) => Promise<void>;
  handleRemoveImage: () => void;
  handleSaveChanges: () => Promise<void>;
};

export type UseUploadImageProps = {
  getImage: () => Promise<LoadedFile | undefined | null>;
  uploadImage: (acceptedFiles: File) => Promise<LoadedFile>;
  removeImage: (deletedImage: LoadedFile) => Promise<void>;
  saveImage: (image: LoadedFile) => Promise<void>;
};

export const useUploadImage = ({
  getImage,
  uploadImage,
  removeImage,
  saveImage,
}: UseUploadImageProps): UseUploadImageReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [initialImage, setInitialImage] = useState<LoadedFile | null>(null);
  const [currentImage, setCurrentImage] = useState<LoadedFile | null>(null);

  const isChanged = useMemo(
    () => !isEqual(initialImage, currentImage),
    [initialImage, currentImage],
  );

  const callWithLoading = async <T>(func: () => Promise<T>): Promise<T> => {
    try {
      setIsLoading(true);
      return await func();
    } finally {
      setIsLoading(false);
    }
  };

  const initializeImage = async () => {
    const image = await getImage();

    if (image) {
      setInitialImage(image);
      setCurrentImage(image);
    }
  };

  useEffect(() => {
    callWithLoading(initializeImage);
  }, []);

  const handleUploadImage = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];

    if (file) {
      const image = await callWithLoading(async () => uploadImage(file));

      setCurrentImage(image);
    }
  };

  const handleRemoveImage = () => {
    setCurrentImage(null);
  };

  const handleSaveChanges = async () => {
    const isImageDeleted = initialImage && !currentImage;
    const isImageUpdated = currentImage && isChanged;

    if (isImageDeleted) {
      await callWithLoading(async () => removeImage(initialImage));
    } else if (isImageUpdated) {
      await callWithLoading(async () => saveImage(currentImage));
    }
  };

  return {
    image: currentImage,
    isLoading,
    isChanged,
    handleUploadImage,
    handleRemoveImage,
    handleSaveChanges,
  };
};
